import { useCallback, useContext, useMemo } from 'react'

import BookmarkCard from '../BookmarkCardNew/BookmarkCard'
import BookmarkLocation from '../BookmarkCardNew/BookmarkLocation'

import BodyText from 'LuxKit/Typography/BodyText'
import ClickTrackerContext from 'contexts/ClickTrackerContext'
import GeoContext from 'contexts/geoContext'
import useImpressionHandler from 'hooks/useImpressionHandler'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import noop from 'lib/function/noop'
import { getImageUrl } from 'lib/image/imageUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import ExperienceBookmarkCardPriceDetails from './ExperienceBookmarkCardPriceDetails'

export type ExperienceBookmarkCardProps = {
  experience: App.ExperienceOffer
  onClick?: () => void
}

function ExperienceBookmarkCard({
  experience,
  onClick = noop,
}: ExperienceBookmarkCardProps) {
  const experienceView = useMemo(() => {
    if (experience) {
      return mapExperienceToBookingView(experience)
    }

    return null
  }, [experience])
  const { image, title } = experienceView ?? {}

  // always prefer ID over URL
  const imageUrl = image?.id ?
      getImageUrl(image?.id, { width: '320px' }) :
    image?.url
  const source = useTripClientSource()
  const offerUrl = addQueryParamsToPath(getExperienceOfferPageURL(experience), {
    source,
  })
  const { currentCurrency } = useContext(GeoContext)

  const ref = useImpressionHandler(experience.id)

  const clickTracker = useContext(ClickTrackerContext)
  const onClickHandler = useCallback(() => {
    clickTracker(experience.id)
    onClick()
  }, [clickTracker, experience.id, onClick])

  // We shouldn't need to check this, but the OfferTypeLabel
  // falls back to 'Lux Exclusive' for always-on experience
  // offers.
  const shouldShowOfferTypeLabel = experienceView?.leExclusive
  return (
    experienceView && (
      <BookmarkCard
        ref={ref}
        title={title}
        body={
          experience.vendor ? (
            <BodyText variant="small">{experience.vendor.name}</BodyText>
          ) : undefined
        }
        bookmarkButton={<ExperienceBookmarkButton offer={experience} />}
        location={
          <BookmarkLocation
            variant="with_experience"
            startLocation={experience?.location.name}
            typeOfExperience={experience?.primaryCategory?.name}
          />
        }
        imageUrl={imageUrl}
        productType={
          shouldShowOfferTypeLabel ? experience.productType : undefined
        }
        offerPriceDetails={
          <ExperienceBookmarkCardPriceDetails
            view={experienceView}
            currencyCode={currentCurrency}
          />
        }
        offerUrl={offerUrl}
        onClick={onClickHandler}
        urgencyLabels={[]}
        isSoldOut={experience.status !== 'ONLINE'}
      />
    )
  )
}

export default ExperienceBookmarkCard
